import { useState, useCallback } from 'react'
import { Plus, Search, Copy, Eye, Send, Trash2, Pencil, X, Save, Loader2, Code, Type, FileText, Mail, ChevronDown } from 'lucide-react'
import { useEditor, EditorContent } from '@tiptap/react'
import { StarterKit } from "@tiptap/starter-kit"
import { Image } from "@tiptap/extension-image"
import { Link } from "@tiptap/extension-link"
import { Underline } from "@tiptap/extension-underline"
import { TextAlign } from "@tiptap/extension-text-align"
import { Color } from "@tiptap/extension-color"
import { TextStyle } from "@tiptap/extension-text-style"
import { Highlight } from "@tiptap/extension-highlight"
import { Placeholder } from "@tiptap/extension-placeholder"
import api from '@/lib/api'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import {
  useEmTemplates, useEmTemplate, useCreateEmTemplate, useUpdateEmTemplate,
  useDeleteEmTemplate, useDuplicateEmTemplate, usePreviewEmTemplate, useSendTestEmail,
  useEmAccounts, type EmTemplate,
} from '@/hooks/useEmailMarketing'

const VARIABLES = [
  { key: 'nome', label: 'Nome' },
  { key: 'sobrenome', label: 'Sobrenome' },
  { key: 'email', label: 'Email' },
  { key: 'empresa', label: 'Empresa' },
  { key: 'cnpj', label: 'CNPJ' },
  { key: 'whatsapp', label: 'WhatsApp' },
  { key: 'curva_abc', label: 'Curva ABC' },
  { key: 'segmento', label: 'Segmento' },
]

const CATEGORIES = ['marketing', 'transacional', 'newsletter', 'eventos', 'follow-up']

// ── TipTap Toolbar ──
function EditorToolbar({ editor }: { editor: any }) {
  if (!editor) return null
  const btnClass = (active: boolean) =>
    `p-1.5 rounded text-xs transition-colors ${active ? 'bg-primary/20 text-primary' : 'hover:bg-accent/50 text-muted-foreground'}`

  return (
    <div className="flex flex-wrap items-center gap-0.5 p-2 border-b bg-muted/20">
      <button onClick={() => editor.chain().focus().toggleBold().run()} className={btnClass(editor.isActive('bold'))} title="Negrito"><strong>B</strong></button>
      <button onClick={() => editor.chain().focus().toggleItalic().run()} className={btnClass(editor.isActive('italic'))} title="Itálico"><em>I</em></button>
      <button onClick={() => editor.chain().focus().toggleUnderline().run()} className={btnClass(editor.isActive('underline'))} title="Sublinhado"><u>U</u></button>
      <button onClick={() => editor.chain().focus().toggleStrike().run()} className={btnClass(editor.isActive('strike'))} title="Riscado"><s>S</s></button>
      <div className="w-px h-5 bg-border mx-1" />
      <button onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} className={btnClass(editor.isActive('heading', { level: 2 }))} title="Título">H2</button>
      <button onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()} className={btnClass(editor.isActive('heading', { level: 3 }))} title="Subtítulo">H3</button>
      <div className="w-px h-5 bg-border mx-1" />
      <button onClick={() => editor.chain().focus().toggleBulletList().run()} className={btnClass(editor.isActive('bulletList'))} title="Lista">• Lista</button>
      <button onClick={() => editor.chain().focus().toggleOrderedList().run()} className={btnClass(editor.isActive('orderedList'))} title="Lista numerada">1. Lista</button>
      <div className="w-px h-5 bg-border mx-1" />
      <button onClick={() => editor.chain().focus().setTextAlign('left').run()} className={btnClass(editor.isActive({ textAlign: 'left' }))} title="Esquerda">⫷</button>
      <button onClick={() => editor.chain().focus().setTextAlign('center').run()} className={btnClass(editor.isActive({ textAlign: 'center' }))} title="Centro">⫿</button>
      <button onClick={() => editor.chain().focus().setTextAlign('right').run()} className={btnClass(editor.isActive({ textAlign: 'right' }))} title="Direita">⫸</button>
      <div className="w-px h-5 bg-border mx-1" />
      <button onClick={() => { const url = prompt('URL do link:'); if (url) editor.chain().focus().setLink({ href: url, target: '_blank' }).run() }} className={btnClass(editor.isActive('link'))} title="Link">🔗</button>
      <button onClick={() => { const url = prompt('URL da imagem:'); if (url) editor.chain().focus().setImage({ src: url }).run() }} className="p-1.5 rounded text-xs hover:bg-accent/50 text-muted-foreground" title="Imagem">📷</button>
      <button onClick={() => editor.chain().focus().setHorizontalRule().run()} className="p-1.5 rounded text-xs hover:bg-accent/50 text-muted-foreground" title="Linha">—</button>
    </div>
  )
}

export default function EmTemplatesPage() {
  const [search, setSearch] = useState('')
  const [catFilter, setCatFilter] = useState('')
  const templates = useEmTemplates({ search: search || undefined, category: catFilter || undefined })
  const createMut = useCreateEmTemplate()
  const updateMut = useUpdateEmTemplate()
  const deleteMut = useDeleteEmTemplate()
  const [confirmDeleteTemplate, setConfirmDeleteTemplate] = useState<EmTemplate | null>(null)
  const dupMut = useDuplicateEmTemplate()
  const previewMut = usePreviewEmTemplate()
  const sendTestMut = useSendTestEmail()
  const accounts = useEmAccounts()

  // Editor state
  const [showEditor, setShowEditor] = useState(false)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [editorTab, setEditorTab] = useState<'visual' | 'html' | 'text'>('visual')
  const [form, setForm] = useState({ name: '', subject: '', body_html: '', body_text: '', category: '', og_image: '', og_description: '', whatsapp_message: '', whatsapp_link_message: '', include_signature: true })

  // Preview / Test
  const [previewHtml, setPreviewHtml] = useState<string | null>(null)
  const [showTestModal, setShowTestModal] = useState(false)
  const [testEmail, setTestEmail] = useState('')
  const [testWithPrefix, setTestWithPrefix] = useState(true) // false = subject SEM "[TESTE]" (útil pra burlar filtro de spam do Outlook/Hotmail)
  const [testPhone, setTestPhone] = useState('')
  const [testChannel, setTestChannel] = useState<'email' | 'whatsapp' | 'both'>('email')
  const [testResult, setTestResult] = useState<{ status: string; message: string } | null>(null)
  const [showVarMenu, setShowVarMenu] = useState(false)

  // TipTap editor
  const editor = useEditor({
    extensions: [
      StarterKit,
      Image,
      Link.configure({ openOnClick: false }),
      Underline,
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
      TextStyle,
      Color,
      Highlight,
      Placeholder.configure({ placeholder: 'Comece a escrever seu email...' }),
    ],
    content: '',
    onUpdate: ({ editor: e }) => {
      setForm(prev => ({ ...prev, body_html: e.getHTML() }))
    },
  }, [showEditor])

  const openNew = () => {
    setEditingId(null)
    setForm({ name: '', subject: '', body_html: '', body_text: '', category: '', og_image: '', og_description: '', whatsapp_message: '', whatsapp_link_message: '' })
    editor?.commands.setContent('')
    setEditorTab('visual')
    setPublicUrl(null)
    setShowEditor(true)
  }

  const openEdit = async (t: EmTemplate | any) => {
    setEditingId(t.id)
    setPublicUrl(null)

    // Fetch full template first, then open editor
    try {
      const { data: full } = await api.get('/v1/email-marketing/templates/' + t.id)
      setForm({
        name: full.name || '',
        subject: full.subject || '',
        body_html: full.body_html || '',
        body_text: full.body_text || '',
        category: full.category || '',
        og_image: full.og_image || '',
        og_description: full.og_description || '',
        whatsapp_message: full.whatsapp_message || '',
        whatsapp_link_message: full.whatsapp_link_message || '',
        include_signature: full.include_signature !== false,
      })
      setPublicUrl(full.public_url || null)
      // Open in HTML tab so complex HTML (tables) shows correctly
      setEditorTab('html')
      setShowEditor(true)
    } catch (e) {
      // Fallback: open with grid data
      setForm({ name: t.name || '', subject: t.subject || '', body_html: '', body_text: '', category: t.category || '', og_image: '', og_description: '', whatsapp_message: '', whatsapp_link_message: '', include_signature: true })
      setEditorTab('html')
      setShowEditor(true)
    }
  }

  const [saving, setSaving] = useState(false)
  const [saveMsg, setSaveMsg] = useState<string | null>(null)
  const [publicUrl, setPublicUrl] = useState<string | null>(null)

  const handleSave = async () => {
    setSaving(true)
    setSaveMsg(null)
    try {
      // Use TipTap only if in visual mode AND body_html is empty (user typed in visual)
      // If user pasted HTML in HTML tab, preserve it even if tab switched to visual
      let html = form.body_html
      if (editorTab === 'visual' && editor) {
        const tiptapHtml = editor.getHTML()
        // Only use TipTap output if it has real content (not just empty paragraph)
        if (tiptapHtml && tiptapHtml !== '<p></p>' && tiptapHtml.length > 10) {
          html = tiptapHtml
        }
      }
      // Fallback: if html is still empty, try TipTap
      if (!html && editor) html = editor.getHTML()
      const payload: Record<string, any> = {
        name: form.name,
        subject: form.subject,
        body_html: html,
        body_text: form.body_text || undefined,
        category: form.category || undefined,
        og_image: form.og_image || null,
        og_description: form.og_description || null,
        whatsapp_message: form.whatsapp_message || null,
        whatsapp_link_message: form.whatsapp_link_message || null,
        include_signature: form.include_signature,
      }

      let result: any
      if (editingId) {
        result = await updateMut.mutateAsync({ id: editingId, ...payload })
      } else {
        result = await createMut.mutateAsync(payload)
        setEditingId(result.id)
      }
      setPublicUrl(result.public_url || null)
      setSaveMsg('Template salvo com sucesso!')
      setShowEditor(false)
    } catch (e: any) {
      const detail = e?.response?.data?.detail
      const msg = typeof detail === 'string' ? detail : (e?.message || JSON.stringify(detail || e))
      alert('Erro ao salvar: ' + msg)
    } finally {
      setSaving(false)
    }
  }

  const handlePreview = async (id: string) => {
    const result = await previewMut.mutateAsync(id)
    setPreviewHtml((result as any).body_html)
  }

  const handleSendTest = async () => {
    if (!editingId) return
    setTestResult(null)
    const results: string[] = []

    try {
      // Send email
      if ((testChannel === 'email' || testChannel === 'both') && testEmail) {
        try {
          const emailRes = await sendTestMut.mutateAsync({ id: editingId, email_to: testEmail, prefix: testWithPrefix }) as any
          results.push(emailRes.status === 'success' ? 'Email enviado com sucesso!' : 'Email erro: ' + emailRes.message)
        } catch (ee: any) {
          results.push('Email erro: ' + (ee?.response?.data?.detail || ee?.message || 'falha'))
        }
      }

      // Send WhatsApp via backend
      if ((testChannel === 'whatsapp' || testChannel === 'both') && testPhone) {
        try {
          const cleanPhone = testPhone.replace(/\D/g, '')
          const { data: waRes } = await api.post(
            '/v1/email-marketing/templates/' + editingId + '/send-test-whatsapp',
            null,
            { params: { phone: cleanPhone } }
          )
          results.push(waRes.status === 'success' ? 'WhatsApp enviado com sucesso!' : 'WhatsApp erro: ' + waRes.message)
        } catch (we: any) {
          results.push('WhatsApp erro: ' + (we?.response?.data?.detail || we?.message || 'falha'))
        }
      }

      setTestResult({
        status: results.some(r => r.includes('erro')) ? 'error' : 'success',
        message: results.join('\n')
      })
    } catch (e: any) {
      setTestResult({ status: 'error', message: e?.message || 'Erro desconhecido' })
    }
  }

  const insertVariable = (key: string) => {
    const varStr = `{{${key}}}`
    if (editorTab === 'visual' && editor) {
      editor.chain().focus().insertContent(varStr).run()
    } else if (editorTab === 'html') {
      setForm(prev => ({ ...prev, body_html: prev.body_html + varStr }))
    } else {
      setForm(prev => ({ ...prev, body_text: prev.body_text + varStr }))
    }
    setShowVarMenu(false)
  }

  // Sync TipTap when switching to visual tab
  const switchTab = (tab: 'visual' | 'html' | 'text') => {
    if (editorTab === 'visual' && editor) {
      setForm(prev => ({ ...prev, body_html: editor.getHTML() }))
    }
    if (tab === 'visual' && editor) {
      editor.commands.setContent(form.body_html || '')
    }
    setEditorTab(tab)
  }

  const items = templates.data || []

  // ── EDITOR VIEW ──
  if (showEditor) {
    return (
      <div className="flex flex-col h-[calc(100vh-64px)]">
        {/* Top bar */}
        <div className="flex items-center justify-between p-3 border-b bg-card flex-shrink-0">
          <div className="flex items-center gap-3">
            <button onClick={() => setShowEditor(false)} className="p-1.5 rounded-lg hover:bg-accent"><X className="w-5 h-5" /></button>
            <h2 className="font-semibold">{editingId ? 'Editar Template' : 'Novo Template'}</h2>
          </div>
          <div className="flex items-center gap-2">
            {editingId && (
              <>
                <button onClick={() => handlePreview(editingId)} className="px-3 py-1.5 text-xs border rounded-lg hover:bg-accent flex items-center gap-1"><Eye className="w-3.5 h-3.5" /> Preview</button>
                <button onClick={() => { setShowTestModal(true); setTestResult(null) }} className="px-3 py-1.5 text-xs border rounded-lg hover:bg-accent flex items-center gap-1"><Send className="w-3.5 h-3.5" /> Enviar Teste</button>
              </>
            )}
            <button onClick={handleSave} disabled={false} className="px-4 py-1.5 text-sm bg-primary text-primary-foreground rounded-lg hover:opacity-90 disabled:opacity-40 flex items-center gap-1">
              <Save className="w-4 h-4" /> Salvar
            </button>
          </div>
        </div>

        {/* Fields + Editor */}
        <div className="flex-1 overflow-auto p-4 space-y-3">
          {/* Meta fields */}
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="text-xs font-medium text-muted-foreground">Nome do template *</label>
              <input value={form.name} onChange={e => setForm({...form, name: e.target.value})} className="w-full mt-1 px-3 py-2 text-sm bg-background border rounded-md" placeholder="Newsletter Abril 2026" />
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground">Assunto do email *</label>
              <input value={form.subject} onChange={e => setForm({...form, subject: e.target.value})} className="w-full mt-1 px-3 py-2 text-sm bg-background border rounded-md" placeholder="Olá {{nome}}, confira nossas novidades" />
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground">Categoria</label>
              <select value={form.category} onChange={e => setForm({...form, category: e.target.value})} className="w-full mt-1 px-3 py-2 text-sm bg-background border rounded-md">
                <option value="">Selecionar...</option>
                {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
          </div>

          {/* Toggle Assinatura — quando ON, o worker injeta a assinatura do remetente (Configurações → SMTP)
              no fim do corpo do e-mail. Quando OFF, o template é enviado exatamente como está. */}
          <div className={`flex items-start justify-between gap-4 p-3 rounded-lg border ${form.include_signature ? 'border-primary/40 bg-primary/5' : 'border-muted bg-muted/30'}`}>
            <div className="flex-1">
              <div className="flex items-center gap-2 text-sm font-medium">
                {form.include_signature ? '✍️ Assinatura ativa' : '🚫 Assinatura desativada'}
              </div>
              <p className="text-xs text-muted-foreground mt-0.5">
                {form.include_signature
                  ? 'O e-mail enviado por este template terá sua assinatura (texto + imagem de Perfil → Integrações de E-mail) anexada automaticamente no final.'
                  : 'Este template será enviado EXATAMENTE como está, sem anexar sua assinatura. Útil para releases internos, transacionais ou e-mails que já trazem assinatura inline no corpo.'}
              </p>
            </div>
            <button
              type="button"
              onClick={() => setForm({...form, include_signature: !form.include_signature})}
              className={`relative inline-flex h-6 w-11 flex-shrink-0 rounded-full transition-colors ${form.include_signature ? 'bg-primary' : 'bg-muted-foreground/30'}`}
              role="switch"
              aria-checked={form.include_signature}
              title={form.include_signature ? 'Clique para REMOVER assinatura deste template' : 'Clique para INSERIR assinatura neste template'}
            >
              <span
                className={`inline-block h-5 w-5 bg-white rounded-full shadow transform transition-transform mt-0.5 ${form.include_signature ? 'translate-x-5' : 'translate-x-0.5'}`}
              />
            </button>
          </div>

          {/* OG Image */}
          <div>
            <label className="text-xs font-medium text-muted-foreground">OG Image (imagem de compartilhamento)</label>
            <div className="flex gap-2 mt-1">
              <input value={form.og_image} onChange={e => setForm({...form, og_image: e.target.value})} className="flex-1 px-3 py-2 text-sm bg-background border rounded-md" placeholder="https://exemplo.com/imagem.png" />
              {form.og_image && <img src={form.og_image} alt="OG" className="w-16 h-10 object-cover rounded border" />}
            </div>
            <p className="text-[10px] text-muted-foreground mt-1">Recomendado: <strong>1200 x 630 pixels</strong> (1.91:1). Formato JPG ou PNG, max 5MB. Exibida como thumbnail ao compartilhar a URL do email em redes sociais (Facebook, LinkedIn, WhatsApp).</p>
          </div>

          {/* OG Description */}
          <div>
            <label className="text-xs font-medium text-muted-foreground">OG Description (descri&ccedil;&atilde;o para compartilhamento)</label>
            <input
              value={form.og_description}
              onChange={e => setForm({...form, og_description: e.target.value})}
              className="w-full mt-1 px-3 py-2 text-sm bg-background border rounded-md"
              placeholder="Breve descri&ccedil;&atilde;o do conte&uacute;do do email para preview em redes sociais"
              maxLength={200}
            />
            <p className="text-[10px] text-muted-foreground mt-1">At&eacute; 200 caracteres. Exibida abaixo do t&iacute;tulo no preview do link (WhatsApp, Facebook, LinkedIn, Twitter).</p>
          </div>

          {/* WhatsApp Message */}
          <div>
            <label className="text-xs font-medium text-muted-foreground">Mensagem WhatsApp (enviada antes do link p&uacute;blico)</label>
            <textarea
              value={form.whatsapp_message}
              onChange={e => setForm({...form, whatsapp_message: e.target.value})}
              className="w-full mt-1 px-3 py-2 text-sm bg-background border rounded-md resize-none"
              rows={3}
              placeholder={"Ol\u00e1 {{nome}}, segue o conte\u00fado exclusivo para a {{empresa}}. Confira:"}
            />
            <p className="text-[10px] text-muted-foreground mt-1">
              Suporta vari&aacute;veis: <code className="text-accent">{"{{nome}}"}</code> <code className="text-accent">{"{{sobrenome}}"}</code> <code className="text-accent">{"{{empresa}}"}</code> <code className="text-accent">{"{{cnpj}}"}</code> <code className="text-accent">{"{{whatsapp}}"}</code> <code className="text-accent">{"{{segmento}}"}</code> <code className="text-accent">{"{{curva_abc}}"}</code>. Enviada como primeira mensagem.
            </p>
          </div>

          {/* WhatsApp Link Message */}
          <div>
            <label className="text-xs font-medium text-muted-foreground">Mensagem do Link WhatsApp (enviada como segunda mensagem, antes do link)</label>
            <input
              value={form.whatsapp_link_message}
              onChange={e => setForm({...form, whatsapp_link_message: e.target.value})}
              className="w-full mt-1 px-3 py-2 text-sm bg-background border rounded-md"
              placeholder="Confira o conte&uacute;do exclusivo:"
            />
            <p className="text-[10px] text-muted-foreground mt-1">
              Texto exibido antes do link p&uacute;blico do email. O link &eacute; enviado como mensagem separada para que o WhatsApp renderize a OG Image (preview do link). Suporta vari&aacute;veis.
            </p>
          </div>

          {/* Tab bar + Variables */}
          <div className="flex items-center justify-between">
            <div className="flex gap-1 bg-muted/50 p-1 rounded-lg">
              <button onClick={() => switchTab('visual')} className={`px-3 py-1.5 rounded-md text-xs font-medium flex items-center gap-1 ${editorTab === 'visual' ? 'bg-card shadow text-foreground' : 'text-muted-foreground'}`}>
                <Type className="w-3.5 h-3.5" /> Visual
              </button>
              <button onClick={() => switchTab('html')} className={`px-3 py-1.5 rounded-md text-xs font-medium flex items-center gap-1 ${editorTab === 'html' ? 'bg-card shadow text-foreground' : 'text-muted-foreground'}`}>
                <Code className="w-3.5 h-3.5" /> HTML
              </button>
              <button onClick={() => switchTab('text')} className={`px-3 py-1.5 rounded-md text-xs font-medium flex items-center gap-1 ${editorTab === 'text' ? 'bg-card shadow text-foreground' : 'text-muted-foreground'}`}>
                <FileText className="w-3.5 h-3.5" /> Texto
              </button>
            </div>

            {/* Variables dropdown */}
            <div className="relative">
              <button onClick={() => setShowVarMenu(!showVarMenu)} className="px-3 py-1.5 text-xs bg-accent/10 text-accent border border-accent/20 rounded-lg font-medium flex items-center gap-1 hover:bg-accent/20">
                {'{{x}}'} Variáveis <ChevronDown className="w-3 h-3" />
              </button>
              {showVarMenu && (
                <div className="absolute right-0 top-full mt-1 bg-card border rounded-lg shadow-xl z-20 py-1 w-48">
                  {VARIABLES.map(v => (
                    <button key={v.key} onClick={() => insertVariable(v.key)} className="w-full text-left px-3 py-1.5 text-sm hover:bg-accent/30 flex items-center justify-between">
                      <span>{v.label}</span>
                      <code className="text-xs text-muted-foreground">{`{{${v.key}}}`}</code>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Editor area */}
          <div className="border rounded-lg overflow-hidden bg-card min-h-[400px]">
            {editorTab === 'visual' && editor && (
              <>
                <EditorToolbar editor={editor} />
                <div className="p-4 prose prose-invert prose-sm max-w-none min-h-[350px] [&_.ProseMirror]:outline-none [&_.ProseMirror]:min-h-[350px] [&_.ProseMirror_p.is-editor-empty:first-child::before]:content-[attr(data-placeholder)] [&_.ProseMirror_p.is-editor-empty:first-child::before]:text-muted-foreground [&_.ProseMirror_p.is-editor-empty:first-child::before]:float-left [&_.ProseMirror_p.is-editor-empty:first-child::before]:h-0 [&_.ProseMirror_p.is-editor-empty:first-child::before]:pointer-events-none">
                  <EditorContent editor={editor} />
                </div>
              </>
            )}
            {editorTab === 'html' && (
              <textarea
                value={form.body_html}
                onChange={e => setForm({...form, body_html: e.target.value})}
                className="w-full h-[450px] p-4 text-sm font-mono bg-transparent border-0 resize-none focus:outline-none"
                placeholder="<html>...</html>"
                spellCheck={false}
              />
            )}
            {editorTab === 'text' && (
              <textarea
                value={form.body_text}
                onChange={e => setForm({...form, body_text: e.target.value})}
                className="w-full h-[450px] p-4 text-sm bg-transparent border-0 resize-none focus:outline-none"
                placeholder="Versão texto puro do email (fallback para clientes que não suportam HTML)"
              />
            )}
          </div>
        </div>

        {/* Preview Modal */}
        {previewHtml && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={() => setPreviewHtml(null)}>
            <div className="absolute inset-0 bg-black/60" />
            <div className="relative w-full max-w-4xl max-h-[85vh] bg-card border rounded-xl shadow-2xl flex flex-col overflow-hidden" onClick={e => e.stopPropagation()}>
              <div className="p-3 border-b flex justify-between items-center flex-shrink-0">
                <span className="text-sm font-semibold">Preview do Template</span>
                <button onClick={() => setPreviewHtml(null)}><X className="w-5 h-5" /></button>
              </div>
              <div className="flex-1 flex gap-4 p-4 overflow-auto">
                <div className="flex-1 min-w-0">
                  <div className="text-xs text-muted-foreground mb-2 font-medium">DESKTOP (600px)</div>
                  <div className="bg-white rounded-lg border overflow-hidden">
                    <iframe srcDoc={previewHtml} className="w-full border-0" style={{ minHeight: '70vh' }} title="Preview Desktop" />
                  </div>
                </div>
                <div className="flex-shrink-0" style={{ width: '206px' }}>
                  <div className="text-xs text-muted-foreground mb-2 font-medium">MOBILE (412x915)</div>
                  <div className="bg-[#1a1a1a] rounded-[20px] border-2 border-[#333] overflow-hidden mx-auto p-1" style={{ width: '206px' }}>
                    <div className="rounded-[16px] overflow-hidden bg-white" style={{ width: '198px', height: '457px' }}>
                      <iframe srcDoc={previewHtml} className="border-0" style={{ width: '412px', height: '915px', transform: 'scale(0.48)', transformOrigin: 'top left' }} title="Preview Mobile" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Send Test Modal */}
        {showTestModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={() => setShowTestModal(false)}>
            <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
            <div className="relative w-full max-w-md bg-card border rounded-xl shadow-2xl p-6" onClick={e => e.stopPropagation()}>
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold flex items-center gap-2"><Send className="w-4 h-4 text-primary" /> Enviar Teste</h3>
                <button onClick={() => setShowTestModal(false)} className="p-1 rounded-lg hover:bg-accent"><X className="w-4 h-4" /></button>
              </div>
              <div className="space-y-3">
                {/* Channel selector */}
                <div>
                  <label className="text-xs font-medium text-muted-foreground mb-1.5 block">Canal de envio</label>
                  <div className="flex gap-1 bg-muted/50 p-1 rounded-lg">
                    <button onClick={() => setTestChannel('email')} className={`flex-1 px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${testChannel === 'email' ? 'bg-card shadow text-foreground' : 'text-muted-foreground'}`}>Email</button>
                    <button onClick={() => setTestChannel('whatsapp')} className={`flex-1 px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${testChannel === 'whatsapp' ? 'bg-card shadow text-foreground' : 'text-muted-foreground'}`}>WhatsApp</button>
                    <button onClick={() => setTestChannel('both')} className={`flex-1 px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${testChannel === 'both' ? 'bg-card shadow text-foreground' : 'text-muted-foreground'}`}>Ambos</button>
                  </div>
                </div>

                {/* Email field */}
                {(testChannel === 'email' || testChannel === 'both') && (
                  <div className="space-y-2">
                    <div>
                      <label className="text-xs font-medium text-muted-foreground">Email *</label>
                      <input value={testEmail} onChange={e => setTestEmail(e.target.value)} type="email" className="w-full mt-1 px-3 py-2 text-sm bg-background border rounded-md" placeholder="seuemail@empresa.com" />
                    </div>
                    <label className="flex items-start gap-2 text-xs cursor-pointer p-2 rounded border border-muted/50 bg-muted/20 hover:bg-muted/40 transition-colors">
                      <input
                        type="checkbox"
                        checked={testWithPrefix}
                        onChange={e => setTestWithPrefix(e.target.checked)}
                        className="mt-0.5 rounded"
                      />
                      <span className="flex-1">
                        <span className="font-medium">Prefixar assunto com <code className="px-1 py-0.5 bg-muted rounded text-[10px]">[TESTE]</code></span>
                        <span className="block text-[11px] text-muted-foreground mt-0.5">
                          Desmarque para ver exatamente como o destinatário vai receber. <strong>Útil pra Hotmail/Outlook</strong> que podem filtrar a palavra "TESTE" como spam.
                        </span>
                      </span>
                    </label>
                  </div>
                )}

                {/* WhatsApp field */}
                {(testChannel === 'whatsapp' || testChannel === 'both') && (
                  <div>
                    <label className="text-xs font-medium text-muted-foreground">WhatsApp *</label>
                    <input value={testPhone} onChange={e => setTestPhone(e.target.value)} className="w-full mt-1 px-3 py-2 text-sm bg-background border rounded-md" placeholder="5516994004674" />
                    <p className="text-[10px] text-muted-foreground mt-0.5">DDI + DDD + Numero (sem +, sem espacos)</p>
                  </div>
                )}

                <p className="text-xs text-muted-foreground">Dados de exemplo: Nome=Joao, Empresa=Empresa Exemplo. Email via SMTP padrao, WhatsApp via Z-API.</p>

                {/* Result */}
                {testResult && (
                  <div className={`p-3 rounded-lg text-sm whitespace-pre-line ${testResult.status === 'success' ? 'bg-green-500/10 text-green-400 border border-green-500/20' : 'bg-red-500/10 text-red-400 border border-red-500/20'}`}>
                    {testResult.message}
                  </div>
                )}
              </div>
              <div className="flex justify-end gap-2 mt-4">
                <button onClick={() => setShowTestModal(false)} className="px-4 py-2 text-sm border rounded-lg">Fechar</button>
                <button onClick={handleSendTest} disabled={sendTestMut.isPending || (testChannel === 'email' && !testEmail) || (testChannel === 'whatsapp' && !testPhone) || (testChannel === 'both' && (!testEmail || !testPhone))} className="px-4 py-2 text-sm bg-primary text-primary-foreground rounded-lg disabled:opacity-40 flex items-center gap-2">
                  {sendTestMut.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  {sendTestMut.isPending ? 'Enviando...' : 'Enviar Teste'}
                </button>
              </div>
            </div>
          </div>
        )}
</div>
    )
  }

  // ── GRID VIEW ──
  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">Templates de Email</h1>
        <button onClick={openNew} className="px-3 py-2 text-sm bg-primary text-primary-foreground rounded-lg flex items-center gap-2 hover:opacity-90">
          <Plus className="w-4 h-4" /> Novo Template
        </button>
      </div>

      {/* Search + Category filter */}
      <div className="flex gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Buscar templates..." className="w-full pl-9 pr-4 py-2 text-sm bg-background border rounded-md" />
        </div>
        <select value={catFilter} onChange={e => setCatFilter(e.target.value)} className="px-3 py-2 text-sm bg-background border rounded-md">
          <option value="">Todas categorias</option>
          {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
      </div>

      {/* Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map(t => {
          const ogImg = (t as any).og_image
          const pubToken = (t as any).public_token
          const pubUrl = pubToken ? 'https://social.myncc.com.br/api/v1/email-marketing/t/' + pubToken : null
          const shortUrl = pubToken ? 'social.myncc.com.br/.../t/' + pubToken.slice(0,8) + '...' : ''
          return (
          <div key={t.id} className="bg-card border rounded-lg overflow-hidden hover:border-primary/50 transition-colors group">
            <div className="flex">
              {/* Left: content */}
              <div className="flex-1 p-4 min-w-0">
                <div className="flex items-start justify-between mb-1">
                  <h3 className="font-semibold text-sm truncate flex-1">{t.name}</h3>
                  {t.category && <span className="text-[10px] px-2 py-0.5 bg-primary/10 text-primary rounded-full flex-shrink-0 ml-2">{t.category}</span>}
                </div>
                <p className="text-xs text-muted-foreground truncate mb-2">{t.subject}</p>
                <div className="text-[10px] text-muted-foreground mb-2">
                  {new Date(t.updated_at).toLocaleDateString('pt-BR')}
                </div>
                {pubUrl && (
                  <div className="flex items-center gap-1 mb-3">
                    <a href={pubUrl} target="_blank" rel="noopener noreferrer" className="text-[11px] text-blue-400 hover:text-blue-300 hover:underline truncate" onClick={e => e.stopPropagation()}>
                      {shortUrl}
                    </a>
                    <button onClick={e => { e.stopPropagation(); navigator.clipboard.writeText(pubUrl || ''); }} className="text-[9px] text-muted-foreground hover:text-foreground px-1.5 py-0.5 border border-border rounded hover:bg-muted flex-shrink-0">
                      Copiar
                    </button>
                  </div>
                )}
                <div className="flex gap-1 opacity-70 group-hover:opacity-100 transition-opacity">
                  <button onClick={() => openEdit(t as EmTemplate)} className="px-2 py-1 text-xs border rounded hover:bg-accent flex items-center gap-1" title="Editar"><Pencil className="w-3 h-3" /> Editar</button>
                  <button onClick={() => handlePreview(t.id)} className="px-2 py-1 text-xs border rounded hover:bg-accent" title="Preview"><Eye className="w-3 h-3" /></button>
                  <button onClick={() => dupMut.mutate(t.id)} className="px-2 py-1 text-xs border rounded hover:bg-accent" title="Duplicar"><Copy className="w-3 h-3" /></button>
                  <button onClick={() => setConfirmDeleteTemplate(t)} className="px-2 py-1 text-xs border rounded hover:bg-accent" title="Excluir"><Trash2 className="w-3 h-3 text-red-500" /></button>
                </div>
              </div>
              {/* Right: OG Image */}
              {ogImg ? (
                <div className="w-32 flex-shrink-0 p-2 flex items-center justify-center">
                  <img src={ogImg} alt={t.name} className="w-full h-auto rounded object-contain max-h-[120px]" />
                </div>
              ) : (
                <div className="w-20 flex-shrink-0 flex items-center justify-center">
                  <Mail className="w-6 h-6 text-primary/20" />
                </div>
              )}
            </div>
          </div>
          )
        })}
        {items.length === 0 && !templates.isLoading && (
          <div className="col-span-full text-center py-16 text-muted-foreground">
            <Mail className="w-12 h-12 mx-auto mb-3 opacity-20" />
            <p>{search || catFilter ? 'Nenhum template encontrado' : 'Nenhum template criado ainda'}</p>
            <button onClick={openNew} className="mt-3 text-sm text-primary hover:underline">Criar primeiro template</button>
          </div>
        )}
      </div>

      {/* Preview Modal (from grid) */}
      {previewHtml && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={() => setPreviewHtml(null)}>
          <div className="absolute inset-0 bg-black/60" />
          <div className="relative w-full max-w-3xl max-h-[85vh] bg-white rounded-xl shadow-2xl overflow-hidden" onClick={e => e.stopPropagation()}>
            <div className="p-3 bg-card border-b flex justify-between items-center">
              <span className="text-sm font-semibold">Preview</span>
              <button onClick={() => setPreviewHtml(null)}><X className="w-5 h-5" /></button>
            </div>
            <iframe srcDoc={previewHtml} className="w-full border-0" style={{ height: '70vh' }} title="Preview" />
          </div>
        </div>
      )}

      <ConfirmDialog
        open={!!confirmDeleteTemplate}
        title="Excluir template?"
        message={`O template "${confirmDeleteTemplate?.name || ''}" será removido. Esta ação não pode ser desfeita.`}
        confirmLabel="Excluir"
        variant="destructive"
        isLoading={deleteMut.isPending}
        onConfirm={async () => {
          if (!confirmDeleteTemplate) return
          await deleteMut.mutateAsync(confirmDeleteTemplate.id)
          setConfirmDeleteTemplate(null)
        }}
        onCancel={() => setConfirmDeleteTemplate(null)}
      />
    </div>
  )
}
